/**
 * Hook para gerenciar pastas de email
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_FOLDERS } from '../constants/folders';
import { getEmailUnreadCount, getEmailFolderCount } from '../services/emailService';
import type { EmailFolder, EmailFolderType } from '../types/email.types';

export const useEmailFolders = () => {
  const [activeFolder, setActiveFolder] = useState<EmailFolderType>('inbox');

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['email-unread-count'],
    queryFn: ({ signal }) => getEmailUnreadCount(signal),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  const { data: spamCount = 0 } = useQuery({
    queryKey: ['email-folder-count', 'spam'],
    queryFn: ({ signal }) => getEmailFolderCount('spam', signal),
    staleTime: 60 * 1000,
    refetchInterval: 120 * 1000,
  });

  const { data: trashCount = 0 } = useQuery({
    queryKey: ['email-folder-count', 'trash'],
    queryFn: ({ signal }) => getEmailFolderCount('trash', signal),
    staleTime: 60 * 1000,
    refetchInterval: 120 * 1000,
  });

  const { data: archiveCount = 0 } = useQuery({
    queryKey: ['email-folder-count', 'archive'],
    queryFn: ({ signal }) => getEmailFolderCount('archive', signal),
    staleTime: 60 * 1000,
    refetchInterval: 120 * 1000,
  });

  const folderCounts: Record<string, number> = useMemo(() => ({
    inbox: unreadCount,
    spam: spamCount,
    trash: trashCount,
    archive: archiveCount,
  }), [unreadCount, spamCount, trashCount, archiveCount]);

  const folders: EmailFolder[] = useMemo(() => {
    return DEFAULT_FOLDERS.map((folder) => ({
      ...folder,
      unreadCount: folderCounts[folder.id] || undefined,
    }));
  }, [folderCounts]);

  const navigateToFolder = useCallback((folderId: EmailFolderType) => {
    setActiveFolder(folderId);
  }, []);

  const activeFolderData = useMemo(() => {
    return folders.find((f) => f.id === activeFolder) || folders[0];
  }, [folders, activeFolder]);

  return {
    folders,
    activeFolder,
    activeFolderData,
    navigateToFolder,
    unreadCount,
    folderCounts,
  };
};
